import React, { useRef, useState } from 'react';
import { Navigation, Pagination, A11y, Autoplay, EffectCoverflow } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { GALLERY } from '../constants/config';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

function Slider() {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // Nav buttons live outside the Swiper element (see .gallery-carousel) so
  // they're never clipped by its overflow-hidden slide track. Swiper needs
  // the real DOM nodes, which only exist after first render, so a state
  // flag forces the one extra render that wires them up.
  const [navReady, setNavReady] = useState(false);

  return (
    <div className="gallery-carousel">
      <button
        ref={(node) => {
          prevRef.current = node;
          if (node && !navReady) setNavReady(true);
        }}
        type="button"
        className="gallery-nav-btn gallery-nav-btn--prev"
        aria-label="Previous project"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <Swiper
        modules={[Navigation, Pagination, A11y, Autoplay, EffectCoverflow]}
        effect="coverflow"
        grabCursor={true}
        centeredSlides={true}
        slidesPerView={1}
        spaceBetween={16}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        pagination={{ clickable: true }}
        loop={GALLERY.images.length > 1}
        autoplay={{
          delay: 3500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        coverflowEffect={{
          rotate: 35,
          stretch: 0,
          depth: 120,
          modifier: 1,
          slideShadows: true,
        }}
        breakpoints={{
          640: {
            slidesPerView: 1.4,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 1.8,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 2.2,
            spaceBetween: 28,
          },
          1280: {
            slidesPerView: 2.5,
            spaceBetween: 32,
          },
        }}
        className="gallery-swiper h-64 w-full sm:h-80 md:h-[420px] lg:h-[480px]"
        aria-label="Project gallery slideshow"
        role="region"
      >
        {GALLERY.images.map((image, index) => (
          <SwiperSlide key={image.src} className="!h-full">
            <img
              loading="lazy"
              src={image.src}
              alt={`${image.label} — slide ${index + 1} of ${GALLERY.images.length}`}
              className="h-full w-full rounded-xl object-cover shadow-2xl ring-1 ring-white/10"
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <button
        ref={nextRef}
        type="button"
        className="gallery-nav-btn gallery-nav-btn--next"
        aria-label="Next project"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
}

export default Slider;
